import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import ItemSopsPanel from '../components/ItemSopsPanel';

type Tab = 'quotes' | 'work-orders' | 'shop-status' | 'employees' | 'sops';

const SOPS_TAB: { key: Tab; label: string } = { key: 'sops', label: 'Item SOPs' };

const TABS: { key: Tab; label: string }[] = [
  { key: 'quotes', label: 'Quotes' },
  { key: 'work-orders', label: 'Work Orders' },
  { key: 'shop-status', label: 'Shop Status' },
  { key: 'employees', label: 'Employees' },
];

const s = {
  page: { padding: '32px 40px', maxWidth: 1200, margin: '0 auto' } as React.CSSProperties,
  header: { marginBottom: 24 } as React.CSSProperties,
  title: { fontSize: 24, fontWeight: 700, margin: 0 } as React.CSSProperties,
  subtitle: { color: '#6b7280', marginTop: 4, fontSize: 14 } as React.CSSProperties,
  layout: { display: 'flex', gap: 24, alignItems: 'flex-start' } as React.CSSProperties,
  sidebar: { width: 200, flexShrink: 0, display: 'flex', flexDirection: 'column' as const, gap: 4 },
  sidebarLink: (active: boolean): React.CSSProperties => ({
    textAlign: 'left',
    padding: '10px 14px',
    borderRadius: 6,
    border: 'none',
    background: active ? '#eef2ff' : 'transparent',
    color: active ? '#3b82f6' : '#374151',
    fontWeight: active ? 700 : 500,
    fontSize: 14,
    cursor: 'pointer',
  }),
  content: { flex: 1, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '28px 32px', minWidth: 0 } as React.CSSProperties,
  h2: { fontSize: 20, fontWeight: 700, marginTop: 0, marginBottom: 8, color: '#111827' } as React.CSSProperties,
  h3: { fontSize: 15, fontWeight: 700, marginTop: 28, marginBottom: 8, color: '#111827' } as React.CSSProperties,
  p: { fontSize: 14, lineHeight: 1.6, color: '#374151', margin: '0 0 12px' } as React.CSSProperties,
  list: { fontSize: 14, lineHeight: 1.7, color: '#374151', paddingLeft: 20, margin: '0 0 12px' } as React.CSSProperties,
  example: { background: '#f9fafb', border: '1px solid #e5e7eb', borderLeft: '3px solid #3b82f6', borderRadius: 6, padding: '12px 16px', margin: '12px 0', fontSize: 13.5, lineHeight: 1.6, color: '#374151' } as React.CSSProperties,
  exampleLabel: { fontWeight: 700, color: '#3b82f6', fontSize: 11, textTransform: 'uppercase' as const, letterSpacing: '0.05em', marginBottom: 4, display: 'block' },
};

function Example({ children }: { children: React.ReactNode }) {
  return (
    <div style={s.example}>
      <span style={s.exampleLabel}>Example</span>
      {children}
    </div>
  );
}

function QuotesDocs() {
  return (
    <>
      <h2 style={s.h2}>Quotes</h2>
      <p style={s.p}>
        A quote covers one room's project (e.g. "Master Bedroom Drapery"). Every quote lives under a job,
        and a job belongs to a customer. Quotes start as <strong>drafts</strong> — where you build up the
        priced items — and become <strong>finalized</strong> quotes once they're ready to send.
      </p>

      <h3 style={s.h3}>Draft Quotes vs. Finalized Quotes</h3>
      <p style={s.p}>
        The Quotes page has two tabs. <strong>Draft Quotes</strong> is your working area — add objects
        (draperies, roman shades, pillows, etc.), enter measurements and fabric, and the system
        auto-calculates fabric yardage, labor, and hardware cost for each one. <strong>Finalized Quotes</strong>{' '}
        are draft quotes that have been locked in — this is what actually gets priced as a single quote and
        can be sent to the customer or pushed to QuickBooks.
      </p>
      <Example>
        You measure a customer's living room for new drapery. Click <strong>New Quote</strong>, pick the job,
        and add a Traversing Drape object with the window width, height, and fabric selection. The Fabrication,
        Installation, and Hardware lines all price themselves from the shop's rate book. Once every window in
        the room is added, click <strong>Finalize</strong> — it moves to the Finalized Quotes tab as one priced
        document.
      </Example>

      <h3 style={s.h3}>Finding a quote</h3>
      <p style={s.p}>Both tabs support:</p>
      <ul style={s.list}>
        <li>A search box (name, job, customer, or status)</li>
        <li><strong>Filter by Job</strong> — narrow the list to one job</li>
        <li>A <strong>Created — Newest/Oldest First</strong> sort</li>
      </ul>

      <h3 style={s.h3}>QuickBooks sync</h3>
      <p style={s.p}>
        On the Finalized Quotes tab, each row's Status column shows a small icon: a cloud-upload icon means
        the quote hasn't been sent to QuickBooks yet — click it to send. Once sent, it becomes a sync icon you
        can click to check whether the customer has viewed or accepted the estimate in QuickBooks.
      </p>
    </>
  );
}

function WorkOrdersDocs() {
  return (
    <>
      <h2 style={s.h2}>Work Orders</h2>
      <p style={s.p}>
        A work order (also called a <strong>traveler</strong>) is a single task built off a finalized quote —
        for example, "Roman shade with blackout lining" for one window. A quote can spawn several work
        orders, one per item that needs to actually be built.
      </p>

      <h3 style={s.h3}>Creating one</h3>
      <p style={s.p}>
        From a finalized quote, click <strong>+ New Work Order</strong>, drag the priced object(s) in, and
        assign it to whoever will build it. It then shows up on that person's Work Orders list and on the
        Shop Status board.
      </p>

      <h3 style={s.h3}>Statuses</h3>
      <p style={s.p}>Every work order is in one of four stages:</p>
      <ul style={s.list}>
        <li><strong>Ready</strong> — queued, not started</li>
        <li><strong>In Progress</strong> — actively being built</li>
        <li><strong>On Hold</strong> — paused (waiting on fabric, hardware, etc.)</li>
        <li><strong>Complete</strong> — finished</li>
      </ul>
      <p style={s.p}>
        Change status right from the list (a dropdown in the Status column) or from the work order's own
        detail page. Admins and office staff can also reassign who a work order is assigned to, from either
        the list or the detail page.
      </p>

      <h3 style={s.h3}>Finding a work order</h3>
      <p style={s.p}>
        Use the search box, or <strong>Filter by Job</strong> and <strong>Filter by Quote</strong> together —
        picking a job first narrows the quote list down to just that job's quotes. There's also a{' '}
        <strong>Created — Newest/Oldest First</strong> sort.
      </p>

      <Example>
        You need to see everything outstanding for the Johnson kitchen remodel. Set{' '}
        <strong>Filter by Job</strong> to "Johnson Kitchen," and the Quote filter automatically only offers
        that job's quotes — pick one to narrow further, or leave it on "All Quotes" to see every work order
        tied to that job.
      </Example>

      <h3 style={s.h3}>Shop workers see less</h3>
      <p style={s.p}>
        If you're logged in as a Shop employee, this page only shows work orders assigned to you, and you
        can update your own status but can't reassign anything — that stays an Office/Admin action.
      </p>
    </>
  );
}

function ShopStatusDocs() {
  return (
    <>
      <h2 style={s.h2}>Shop Status</h2>
      <p style={s.p}>
        A real-time board of every work order, grouped into four columns — Ready, In Progress, On Hold, and
        Complete Today — so you can see the whole shop floor at a glance. "Complete Today" only shows things
        finished today; it resets each day rather than piling up forever.
      </p>

      <h3 style={s.h3}>Changing status</h3>
      <p style={s.p}>
        Drag a card from one column and drop it in another — that updates its status immediately, the same
        as changing it from the Work Orders list.
      </p>

      <h3 style={s.h3}>Prioritizing the Ready column</h3>
      <p style={s.p}>
        Each employee's Ready work orders can be ranked independently. Drag a card and drop it above or
        below another Ready card <strong>assigned to the same person</strong> — the list shifts live as you
        drag, and the top of that person's stack is what they should pick up first.
      </p>
      <Example>
        Mike has four roman shades sitting in Ready. The customer waiting on the third one calls asking for a
        rush — drag that card to the top of Mike's list. He now sees it as his first job, without anyone
        needing to change its due date or reassign anything.
      </Example>

      <h3 style={s.h3}>Filtering the board</h3>
      <ul style={s.list}>
        <li><strong>Filter by Job</strong> and <strong>Filter by Quote</strong> — work together, same as the Work Orders page</li>
        <li><strong>Filter by Assignee</strong> (Admin/Office only) — narrow the whole board down to one person, or "Everyone"</li>
      </ul>

      <h3 style={s.h3}>Assigning work</h3>
      <p style={s.p}>
        Admins and office staff can reassign a card right from its dropdown on the board. Anyone — admin,
        office, or shop — can be assigned a work order, including the more complicated ones that sometimes
        get handed to a manager or admin to finish.
      </p>

      <h3 style={s.h3}>Shop workers see their own board</h3>
      <p style={s.p}>
        Logged in as a Shop employee, the board only shows your own work orders (no Assignee filter, since
        there's nothing to filter), and you can't reassign cards — but you can still drag to change status or
        reorder your own Ready list.
      </p>
    </>
  );
}

function EmployeesDocs() {
  return (
    <>
      <h2 style={s.h2}>Employees</h2>
      <p style={s.p}>
        Manage the accounts people use to log in. This page is only visible to Admins.
      </p>

      <h3 style={s.h3}>Adding someone</h3>
      <p style={s.p}>
        Click <strong>+ Add Employee</strong>, fill in their name, email, and a password, and pick a role:
      </p>
      <ul style={s.list}>
        <li><strong>Admin</strong> — full access to everything, including this Employees page</li>
        <li><strong>Office</strong> — everything except the Employees page (quotes, work orders, customers, shop status, settings, etc.)</li>
        <li><strong>Shop</strong> — only Work Orders and Shop Status, and only the work assigned to them</li>
      </ul>

      <Example>
        You hire a new installer who only needs to see their own jobs for the day. Add them as an employee
        with the <strong>Shop</strong> role — when they log in, the nav bar itself only shows Work Orders and
        Shop Status, and both are already filtered down to just their own assignments.
      </Example>

      <h3 style={s.h3}>Changing a role later</h3>
      <p style={s.p}>
        Promoting someone (say, from Shop to Office) or adjusting access is done the same way any account
        detail would be edited — update their role and it takes effect the next time they load the app.
      </p>
    </>
  );
}

export default function Documentation() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  // Item SOPs is for everyone except shop-floor workers (same rule the
  // header used for its own link before this was merged in here).
  // Item SOPs comes first and is the default tab for everyone who can see it;
  // workers don't get it, so their default stays Quotes.
  const tabs = user?.role === 'worker' ? TABS : [SOPS_TAB, ...TABS];
  const requested = params.get('tab');
  const [activeTab, setActiveTab] = useState<Tab>(
    tabs.some((t) => t.key === requested) ? (requested as Tab) : tabs[0].key
  );

  return (
    <div style={s.page}>
      <div style={s.header}>
        <h1 style={s.title}>Documentation</h1>
        <p style={s.subtitle}>How to use Quotes, Work Orders, Shop Status, and Employees{user?.role !== 'worker' ? ', plus the quoting SOP for each item type' : ''}</p>
      </div>

      <div style={s.layout}>
        <div style={s.sidebar}>
          {tabs.map((tab) => (
            <button
              key={tab.key}
              style={s.sidebarLink(activeTab === tab.key)}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={s.content}>
          {activeTab === 'quotes' && <QuotesDocs />}
          {activeTab === 'work-orders' && <WorkOrdersDocs />}
          {activeTab === 'shop-status' && <ShopStatusDocs />}
          {activeTab === 'employees' && <EmployeesDocs />}
          {activeTab === 'sops' && <ItemSopsPanel />}
        </div>
      </div>
    </div>
  );
}
